import defaultTo from 'lodash/defaultTo';
import moment from 'moment';
import classNames from 'classnames';
import React, { Fragment, useCallback, useMemo } from 'react';
import get from 'lodash/get';
import { addDays, format, parse } from 'date-fns';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Badge from '@atlaskit/badge';
import EmptyState from '@atlaskit/empty-state';
import Form, { Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import { Radio } from '@atlaskit/radio';
import { Box, Flex, Text } from '@atlaskit/primitives';
import { analytics, AnalyticsTrackSubject, AnalyticsTrackAction } from '@atlassian/temp-analytics';
import {
  CustomOrderEntitlement,
  getChargeElement,
  getLinkedEntitlementsDetails,
  getParentContainerId,
  currentEntitlementType,
} from '@css-xp/base/entitlement-utils';
import {
  BillingFrequency,
  BillingStatus,
  EntitlementDetails,
  EntitlementGroup,
  FetchEntitlementsByBillingDateQuery,
  FetchEntitlementsByBillingDateQueryVariables,
  LinkedDestinationEntitlement,
  LinkedProductTypes,
  ProvisionedResourceType,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  EntitlementIcon,
  ErrorComponents,
  errorNotificationBuilder,
  FormattedDate,
  getFormattedDateString as getFormattedUTCDateString,
  InfoPanel,
  NotificationTypes,
  successNotificationBuilder,
  SummaryPanelContainer,
  SummaryPanelRow,
  useFeatureToggle,
  useFormSubmit,
  useNotification,
  useSidebar,
} from '@css-xp/base';
import { HelpContent, NoData } from '@css-xp/ppc-common';
import { getDestinationLinkedEntitlementsType, isCollectionProductEntitlement } from '@css-xp/ppc-utils';
import { WarningDialog } from '@css-xp/entitlements-v2/common/sidebar-panel/warning-dialog';
import { fetchEntitlementsByBillingDate } from '@css-xp/base/common-sidebars/src/queries/fetch-entitlements-by-billing-date';
import { HighlightedDatePicker } from '@css-xp/base/date-picker';
import { SignalFxFEPerfStore } from '@atlassian/temp-helper/constants';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { logTimeStamp } from '@atlassian/temp-metal';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { OrderItemType } from '@atlassian/cssxp-types-entitlement';
import { updateEntitlementPlan, updateEntitlementPlanV2 } from '@atlassian/temp-proxies';
import { NEW_DESIGN_DATE_FORMAT, UpdateOrderRequest, NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { getUserTier } from '@atlassian/cssxp-utils-entitlement';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { useConsentHub } from '@css-xp/ppc-consent-hub';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import { LinkedEntitlementsTable } from '../../components/linked-entitlements-table';
import { SubscriptionsByBillDate } from '../../components/subscriptions-by-bill-date';
import './set-co-bill-date.scss';

const DATE_FORMAT = 'YYYY-MM-DD';

interface Props {
  invoiceGroupId: string;
  product: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  isMetaDataHidden?: boolean;
  isAddOnChangesEnabled?: boolean;
  isSandboxSupportForSubscriptionsEnabled?: boolean;
  isSandboxDecouplingEnabled?: boolean;
}

interface FormData {
  newSelectedBillDate?: string | null;
  supportTicketNumber?: string | null;
  handNote?: string | null;
}

const infoMessageForLinkedEntsTable =
  'Performing this action will automatically change the bill date of these subscriptions:';

const SetCoBillDate: React.FC<Props> = ({
  invoiceGroupId,
  product,
  refetch,
  isAddOnChangesEnabled,
  isSandboxSupportForSubscriptionsEnabled,
  isSandboxDecouplingEnabled = false,
}) => {
  const {
    productId,
    id,
    latestPeriodEndDate: latestPeriodEndDateForEntitlement,
    transactionAccountId,
    billingFrequency,
    entitlementNumber,
    subscriptionTimestamps,
    relatesFromEntitlements,
    provisionedResourceDetails,
    isSandbox,
  } = product;

  // if latest product period end date is not available then we have to use last trial end timestamp
  let subscriptionEndDate = latestPeriodEndDateForEntitlement || subscriptionTimestamps?.lastTrialEndTimestamp;

  const subscriptionCurrentBillDate = subscriptionEndDate;

  const relatesFromEntitlementIdOrProductId = relatesFromEntitlements?.[0]?.entitlementId || id;

  const [selectedBillingDate, setSelectedBillingDate] = React.useState('');
  const [isConfirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [linkedEntitlementsData, setLinkedEntitlementsData] = React.useState<Array<LinkedDestinationEntitlement>>([]);
  const [visibleLinkedEntitlements, setVisibleLinkedEntitlements] = React.useState<number>();
  const [radioValue, setRadioValue] = React.useState<string>('new-bill-date');
  const [loadingLinkedEntitlement, setLoadingLinkedEntitlements] = React.useState(false);

  const { closeSidebar } = useSidebar();

  const shouldShowOrgName = useMemo(
    () =>
      !!provisionedResourceDetails &&
      provisionedResourceDetails?.provisionedResourceType === ProvisionedResourceType.ORGANISATION,
    [provisionedResourceDetails]
  );

  const [isEnabled] = useFeatureToggle();
  const isOmsV2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MANAGE_SUBSCRIPTION_OMS_V2);
  const isJPDAsJiraFamilyEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY);
  const isDesignIteration1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1);
  const isTWCPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2);

  const isCollectionProduct = isTWCPhase2Enabled && isCollectionProductEntitlement(product?.productId);

  // Get the max latest period end date from linked entitlements
  const maxBillDateForLinkedEntitlementTimestamp = linkedEntitlementsData?.reduce((current, linkedEntitlement) => {
    const linkedSubscriptionEndDate =
      linkedEntitlement?.latestPeriodEndDate || linkedEntitlement?.subscriptionTimestamps?.lastTrialEndTimestamp;
    const linkedRelatesFromEntitlementId = get(
      linkedEntitlement,
      'relatesFromEntitlements.[0].entitlementId',
      linkedEntitlement.entitlementId
    );
    if (linkedSubscriptionEndDate && linkedRelatesFromEntitlementId === relatesFromEntitlementIdOrProductId) {
      const linkedSubscriptionEndDateTimeStamp = moment.utc(linkedSubscriptionEndDate).valueOf();
      if (current < linkedSubscriptionEndDateTimeStamp) {
        current = linkedSubscriptionEndDateTimeStamp;
      }
    }
    return current;
  }, 0);

  // if max bill date for linked entitlements is greater than current subscription end date then we have to use that
  if (
    maxBillDateForLinkedEntitlementTimestamp > 0 &&
    subscriptionCurrentBillDate &&
    maxBillDateForLinkedEntitlementTimestamp > moment.utc(subscriptionCurrentBillDate).valueOf()
  ) {
    subscriptionEndDate = maxBillDateForLinkedEntitlementTimestamp.toString();
  }

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<any, any>({
    submitFormAction: isOmsV2Enabled ? updateEntitlementPlanV2 : updateEntitlementPlan,
  });

  const { updateCustomerConsent } = useConsentHub();

  const isEntitlementRelationshipExists = useMemo(() => {
    return product?.relatesToEntitlements || product?.relatesFromEntitlements;
  }, [product]);

  const { sendSuccessNotification, sendErrorNotification, notificationSent, setNotificationSent } = useNotification();

  const sendChangeSuccessTrackEvent = () => {
    analytics.sendTrackEvent({
      action: AnalyticsTrackAction.CHANGED,
      actionSubject: AnalyticsTrackSubject.CHANGE_BILL_DATE,
      attributes: {
        entitlementId: id,
      },
    });
  };

  const sendChangeFailedTrackEvent = () => {
    analytics.sendTrackEvent({
      action: AnalyticsTrackAction.FAILED,
      actionSubject: AnalyticsTrackSubject.CHANGE_BILL_DATE,
      attributes: {
        entitlementId: id,
      },
    });
  };

  const getNotificationOption = (id: string) => {
    const notificationOptions = successNotificationBuilder();
    return notificationOptions
      ?.withId(id)
      .withHeader('Update successful')
      .withMessage('This bill date change will be in effect from the next billing cycle.')
      .withDismissAfter(5000)
      .isVersion(NotificationTypes.FLAG)
      .build();
  };

  if (complete && !notificationSent) {
    const notificationOptions = getNotificationOption('set-bill-date-success');
    sendChangeSuccessTrackEvent();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeSidebar();
    refetch();
  }

  let nextBillDate = moment.utc().format(NEW_DESIGN_DATE_FORMAT);
  if (subscriptionEndDate) {
    const formattedNextBillDate = getFormattedUTCDateString({
      date: subscriptionEndDate,
      format: NEW_DESIGN_DATE_FORMAT,
    });
    if (formattedNextBillDate) {
      nextBillDate = formattedNextBillDate;
    }
  }

  const constructRequestData = useCallback(() => {
    const requestData: UpdateOrderRequest = {
      entitlementId: id,
      productId: productId || '',
      nextBillingAnchorTimestamp: selectedBillingDate
        ? moment.utc(selectedBillingDate).valueOf().toString()
        : new Date(nextBillDate).getTime().toString(),
      metadata: {},
      units: isStatuspage(product?.productId)
        ? (product?.unitCount as string)
        : getUserTier(product?.subscription)?.toString(),
      chargeElement: getChargeElement(product?.productId as string),
      //send isUpgradeTrial if subscription is in trial
      ...(!!product?.upgradeTrial && { isUpgradeTrialOrder: !!product?.upgradeTrial }),
    };

    if (isEntitlementRelationshipExists) {
      requestData.linkedEntitlements = linkedEntitlementsData.length
        ? getLinkedEntitlementsDetails(
            product,
            linkedEntitlementsData.filter(
              (linkedEntitlementObj) => linkedEntitlementObj.billingStatus === BillingStatus.ACTIVE
            )
          )
        : [];
    }
    return requestData;
  }, [id, productId, selectedBillingDate, isEntitlementRelationshipExists, linkedEntitlementsData, product]);

  const constructRequestDataV2 = useCallback(() => {
    const nextBillingAnchorTimestamp = selectedBillingDate
      ? moment.utc(selectedBillingDate).valueOf()
      : new Date(nextBillDate).getTime();
    const request: CustomOrderEntitlement = {
      entitlementId: id,
      orderType: OrderItemType.AMENDMENT_ORDER,
      productId: productId || '',
      offeringId: product?.offeringId as string,
      nextBillingAnchorTimestamp,
    };

    const entitlementsList: CustomOrderEntitlement[] = [];
    entitlementsList.push(request);
    if (isEntitlementRelationshipExists) {
      linkedEntitlementsData?.forEach((linkedEntitlement) => {
        entitlementsList.push({
          entitlementId: linkedEntitlement.entitlementId,
          orderType: OrderItemType.AMENDMENT_ORDER,
          productId: linkedEntitlement.productId as string,
          offeringId: linkedEntitlement.offeringId as string,
          nextBillingAnchorTimestamp,
        });
      });
    }
    const requestPayloadV2 = {
      invoiceGroupId: defaultTo(product?.billingGroupId, ''),
      transactionAccountId: defaultTo(product?.transactionAccountId, ''),
      entitlements: entitlementsList,
    };
    return requestPayloadV2;
  }, [id, productId, selectedBillingDate, isEntitlementRelationshipExists, linkedEntitlementsData, product]);

  const handleFormSubmit = useCallback(async () => {
    logTimeStamp(new Date().getTime(), 'Set Bill Date', SignalFxFEPerfStore.FE_USER_TASK_SET_BILL_DATE_TIME);
    const selectedBillDate = getFormattedUTCDateString({ date: selectedBillingDate, format: NEW_DESIGN_DATE_FORMAT });
    const subscriptionEndBillDate = getFormattedUTCDateString({
      date: subscriptionEndDate,
      format: NEW_DESIGN_DATE_FORMAT,
    });
    if (selectedBillDate !== subscriptionEndBillDate) {
      setConfirmDialogOpen(true);
    } else {
      try {
        const requestData = isOmsV2Enabled ? constructRequestDataV2() : constructRequestData();
        await updateCustomerConsent(`By selecting ‘Confirm' below, you acknowledge that the bill date will also change for all
                          product and app subscriptions on the same site that belong to the same product family. Your new bill date will be in effect from the next billing cycle. Pro-rata adjustments will be
                          applied to the next invoice.`);
        submitForm(requestData);
      } catch (err) {
        const errorNotificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withId('set-bill-date-error')
          .withHeader("Sorry, we couldn't set the new bill date.")
          .withMessage('Some error has occurred')
          .withDismissAfter(5000)
          .build();
        sendChangeFailedTrackEvent();
        sendErrorNotification(errorNotificationOptions);
        setNotificationSent(true);
      }
    }
  }, [constructRequestData, subscriptionEndDate, selectedBillingDate, submitForm]);

  const onConfirmDialog = async () => {
    try {
      setConfirmDialogOpen(false);
      const requestData = isOmsV2Enabled ? constructRequestDataV2() : constructRequestData();
      await updateCustomerConsent(`By selecting ‘Confirm' below, you acknowledge that the bill date will also change for all
                          product and app subscriptions on the same site that belong to the same product family. Your new bill date will be in effect from the next billing cycle. Pro-rata adjustments will be
                          applied to the next invoice.`);

      submitForm(requestData);
    } catch {
      const errorNotificationOptions = errorNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withId('set-bill-date-error')
        .withHeader("Sorry, we couldn't set the new bill date.")
        .withMessage('Some error has occurred')
        .withDismissAfter(5000)
        .build();
      sendChangeFailedTrackEvent();
      sendErrorNotification(errorNotificationOptions);
      setNotificationSent(true);
    }
  };

  const { data, loading, error } = useQuery<
    FetchEntitlementsByBillingDateQuery,
    FetchEntitlementsByBillingDateQueryVariables
  >(fetchEntitlementsByBillingDate, {
    variables: {
      id: invoiceGroupId,
      entitlementId: id,
      filterOptions: [{ key: 'showTrialEntitlements', value: 'true' }],
    },
  });

  const addDaysToCurrentDate = (dateStr: string, numberOfDaysToAdd: number) => {
    const dateFormat = 'MMM dd, yyyy';

    // Parse the date string into a Date object
    const parsedDate = parse(dateStr, dateFormat, new Date());

    // Add days to the parsed date
    const updatedDateWithAddedDays = addDays(parsedDate, numberOfDaysToAdd);

    // Format the updated date
    const formattedDate = format(updatedDateWithAddedDays, dateFormat);
    return formattedDate;
  };

  const upcomingFinalBillDate = useMemo(() => {
    if (subscriptionCurrentBillDate) {
      const latestPeriodDate = getFormattedUTCDateString({ date: subscriptionCurrentBillDate });
      let upcomingBillDate = moment.utc(latestPeriodDate).add(1, 'months');
      if (billingFrequency === BillingFrequency.ANNUAL) {
        upcomingBillDate = moment.utc(latestPeriodDate).add(1, 'years');
      }
      upcomingBillDate = moment.utc(upcomingBillDate).subtract(1, 'days');
      return upcomingBillDate;
    }
  }, [billingFrequency, subscriptionCurrentBillDate]);

  const handleDisableDateFilter = (date: string) => {
    if (subscriptionEndDate) {
      // considering only YYYY-MM-DD from latestPeriodEndDate
      const latestPeriodDate = getFormattedUTCDateString({ date: subscriptionEndDate, format: DATE_FORMAT });
      const currentStartOfDay = moment.utc(date);
      if (currentStartOfDay.isBefore(latestPeriodDate)) {
        return true;
      }
      if (upcomingFinalBillDate && currentStartOfDay.isAfter(upcomingFinalBillDate)) {
        return true;
      }
    }
    return false;
  };

  const entitlementsGroupByBillingDate = data?.entitlementsGroupByBillingDate;

  const billingDateGroup = useMemo(() => {
    // Filter the billing date group based on the next bill bate and upcoming final bill date
    return (entitlementsGroupByBillingDate?.billingDateGroup ?? [])?.filter((group) => {
      if (!group?.billingDate || !upcomingFinalBillDate) return false;
      const billDate = getFormattedUTCDateString({ date: group.billingDate, format: DATE_FORMAT });
      const subscriptionNextBillDate = getFormattedUTCDateString({ date: nextBillDate, format: DATE_FORMAT });
      const subscriptionUpcomingFinalBillDate = getFormattedUTCDateString({
        date: upcomingFinalBillDate.toString(),
        format: DATE_FORMAT,
      });
      // bill dates should be in between latestPeriodDate and upcomingBillDate
      return moment(billDate).isBetween(
        subscriptionNextBillDate,
        moment.utc(subscriptionUpcomingFinalBillDate).format(DATE_FORMAT),
        null,
        '[]'
      );
    });
  }, [entitlementsGroupByBillingDate, nextBillDate, upcomingFinalBillDate]);

  const getDescription = useCallback(() => {
    const billingDateEntitlements = (billingDateGroup as EntitlementGroup[])
      .filter((entitlementObj) => entitlementObj.billingDate === selectedBillingDate)
      .map((entitlementObj) => entitlementObj.entitlements);

    return (
      <div>
        <div>
          {`Bill date of ${entitlementNumber} will change from `}
          <FormattedDate date={subscriptionCurrentBillDate} format={NEW_DATE_FORMAT} />
          {` to `} <FormattedDate date={selectedBillingDate} format={NEW_DATE_FORMAT} />.
        </div>
        <div>The new bill date will be in effect from the next billing cycle.</div>
        <div>
          <br />
          <div className="mb-8">By selecting &apos;Confirm&apos; below, you acknowledge:</div>
          <ul className="entitlement-list-data">
            <li>
              The bill date will also change for all product{isAddOnChangesEnabled ? ', app and add-on' : ' and app'}{' '}
              subscriptions on the same site that belong to the same product family.
            </li>
            {billingDateEntitlements && billingDateEntitlements?.length > 0 && (
              <li>It will be co-billed with other selected subscriptions.</li>
            )}
            <li>Pro-rata adjustments will be applied to the next invoice.</li>
          </ul>
        </div>
      </div>
    );
  }, [entitlementNumber, subscriptionCurrentBillDate, selectedBillingDate, billingDateGroup]);

  if (loading) return <LoadingIndicator />;

  if (error) {
    const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);
    return <GenericError error={error} />;
  }

  const handleRadioChange = ({ currentTarget: { value } }: React.SyntheticEvent<any>, formData?: FormData) => {
    if (value !== 'new-bill-date') {
      setSelectedBillingDate(value);
    } else {
      setSelectedBillingDate(formData?.newSelectedBillDate ?? nextBillDate);
    }
    setRadioValue(value);
  };

  const subscriptionsOnBillDate = () => {
    let totalEntitlementsCounts = 0;
    const subscriptionsOnDate = billingDateGroup.map((group) => {
      const { billingDate, entitlements = [] } = group as EntitlementGroup;

      /**
       * Below logic is to filter out date suggestion which is same as current subscription next bill date.
       * Refer -> https://hello.jira.atlassian.cloud/browse/TTC-29996 for more details.
       */
      const currentSubscriptionNextBillDate = getFormattedUTCDateString({
        date: subscriptionCurrentBillDate,
        format: 'DD',
      });
      const otherSubscriptionBillDate = getFormattedUTCDateString({ date: billingDate, format: 'DD' });
      const isOtherSubscriptionsBillDateSameAsCurrent = otherSubscriptionBillDate === currentSubscriptionNextBillDate;

      const entitlementsFilterOnIds = (entitlements as EntitlementDetails[]).filter(
        (item) => item?.entitlementNumber !== product?.entitlementNumber
      );

      /**
       * Since we are filtering out date suggestion which is same as current subscription next bill date,
       * below code is to avoid increase count for entitlements for such bill date.
       */
      if (!isOtherSubscriptionsBillDateSameAsCurrent) {
        totalEntitlementsCounts += entitlementsFilterOnIds?.length ?? 0;
      }
      return (
        entitlementsFilterOnIds &&
        entitlementsFilterOnIds?.length > 0 &&
        !isOtherSubscriptionsBillDateSameAsCurrent && (
          <div className="subscription-billing-container" key={`key-bill-date-${billingDate}`}>
            <div className="radio-button-container">
              <Radio
                isChecked={billingDate === radioValue}
                onChange={(event) => handleRadioChange(event)}
                name={`key-${billingDate}`}
                value={billingDate ?? ''}
              />
            </div>
            <SubscriptionsByBillDate
              key={`key-${billingDate}`}
              billingDate={billingDate as string}
              entitlements={entitlementsFilterOnIds}
              focused={billingDate === selectedBillingDate}
              txaId={transactionAccountId as string}
              isDesignIteration1Enabled={isDesignIteration1Enabled}
            />
          </div>
        )
      );
    });
    if (totalEntitlementsCounts === 0) {
      return <EmptyState header={'No subscriptions found'} />;
    }
    return subscriptionsOnDate;
  };

  const renderCurrentPlanDetails = () => {
    return (
      <>
        <Text weight="bold">Current plan</Text>
        <SummaryPanelRow
          label={
            <Text color="color.text.subtle" size="small" weight="bold">
              Product name
            </Text>
          }
          value={
            <Flex alignItems="center" gap="space.100">
              <EntitlementIcon
                logoId={product?.logoId}
                productId={product?.productId}
                className="avatar medium"
                isCloud={true}
              />
              <div className="flex-column">
                <p className="text-ellipsis">
                  <Text color="color.text.subtle">{product?.productName} </Text>{' '}
                </p>
                <Text size="small" color="color.text.subtlest">
                  {product?.entitlementNumber}
                </Text>
              </div>
            </Flex>
          }
        />
        <div className="linked-subscriptions-list">
          <LinkedEntitlementsTable
            header={
              <Box paddingInlineStart="space.400">
                <Flex gap="space.100">
                  <Badge>{visibleLinkedEntitlements ? visibleLinkedEntitlements - 1 : 0}</Badge>
                  <Text size="small" weight="bold">
                    {isCollectionProduct ? 'included products' : 'linked subscriptions'}
                  </Text>
                </Flex>
              </Box>
            }
            showBillDateColumn={true}
            message={infoMessageForLinkedEntsTable}
            messageType="information"
            entitlementId={product?.id ?? ''}
            parentContainerID={getParentContainerId(product)}
            productType={product.productType as LinkedProductTypes}
            currentEntitlementType={currentEntitlementType(product)}
            destinationEntitlementType={getDestinationLinkedEntitlementsType(isSandboxDecouplingEnabled, !!isSandbox)}
            findApps={true}
            findProducts={true}
            updateLinkedEntitlements={setLinkedEntitlementsData}
            setLoading={setLoadingLinkedEntitlements}
            transactionAccountId={transactionAccountId}
            removeAddOnEntitlements={!isAddOnChangesEnabled}
            isSandboxSupportForSubscriptionsEnabled={isSandboxSupportForSubscriptionsEnabled}
            hideInfoMessage={true}
            linkedEntsCount={setVisibleLinkedEntitlements}
            isDesignIteration1Enabled={isDesignIteration1Enabled}
          />
        </div>
      </>
    );
  };

  const renderBillDateInfoMessage = () => {
    return isDesignIteration1Enabled ? (
      'New bill date'
    ) : (
      <>
        Select a new bill date between {nextBillDate && addDaysToCurrentDate(nextBillDate, 1)}
        {upcomingFinalBillDate && (
          <>
            {` - `}
            <FormattedDate date={upcomingFinalBillDate.toString()} format={NEW_DESIGN_DATE_FORMAT} />
          </>
        )}
      </>
    );
  };

  const renderNewBillDateTite = () => {
    return (
      isDesignIteration1Enabled && (
        <Text weight="bold" color="color.text.subtle" size="small">
          Select date
        </Text>
      )
    );
  };

  const renderNewBillDatePlaceholder = () => {
    return isDesignIteration1Enabled ? 'Select' : 'Select date...';
  };

  const renderMatchedSubscriptionsTitle = () => {
    return isDesignIteration1Enabled ? (
      <div className="ml-32">
        <Text weight="bold" color="color.text.subtle" size="small">
          Bill date matching other subscriptions
        </Text>
      </div>
    ) : (
      <h4 id="co-bill-sticky-subheader" className="sticky-subheading">
        Or select a date that matches other subscriptions
      </h4>
    );
  };

  return (
    <>
      <div
        className={classNames('set-bill-date-container', {
          'set-bill-date-container-v3': isDesignIteration1Enabled,
        })}
        data-testid="set-bill-date-container"
      >
        <div className="set-bill-date-info">
          <div className="header">
            <FormHeader title="Change bill date" />
          </div>
          <div className="product-detail-info">
            <SummaryPanelContainer isHeaderContentHidden={true}>
              {isDesignIteration1Enabled ? (
                renderCurrentPlanDetails()
              ) : (
                <>
                  <SummaryPanelRow label="Product name" value={defaultTo(product?.productName, 'Unnamed product')} />
                  <SummaryPanelRow label="Entitlement number" value={product?.entitlementNumber} />
                  {shouldShowOrgName ? (
                    <SummaryPanelRow
                      label="Org name"
                      value={provisionedResourceDetails?.organisationName ?? <NoData />}
                    />
                  ) : (
                    <SummaryPanelRow label="Site name" value={product?.cloudSiteHostname ?? <NoData />} />
                  )}
                  {subscriptionEndDate && (
                    <SummaryPanelRow
                      label={'Current bill date'}
                      value={<FormattedDate date={subscriptionCurrentBillDate} format={NEW_DESIGN_DATE_FORMAT} />}
                    />
                  )}
                </>
              )}
            </SummaryPanelContainer>
          </div>
          {!isDesignIteration1Enabled && isEntitlementRelationshipExists && (
            <div className="entitlement-relation-table">
              <LinkedEntitlementsTable
                showBillDateColumn={true}
                message={infoMessageForLinkedEntsTable}
                messageType="information"
                entitlementId={product?.id ?? ''}
                parentContainerID={getParentContainerId(product, undefined, isJPDAsJiraFamilyEnabled)}
                productType={product.productType as LinkedProductTypes}
                currentEntitlementType={currentEntitlementType(product)}
                destinationEntitlementType={getDestinationLinkedEntitlementsType(
                  isSandboxDecouplingEnabled,
                  !!isSandbox
                )}
                findApps={true}
                findProducts={true}
                updateLinkedEntitlements={setLinkedEntitlementsData}
                setLoading={setLoadingLinkedEntitlements}
                transactionAccountId={transactionAccountId}
                removeAddOnEntitlements={!isAddOnChangesEnabled}
                isSandboxSupportForSubscriptionsEnabled={isSandboxSupportForSubscriptionsEnabled}
                productId={productId ?? ''}
                isJPDAsJiraFamilyEnabled={isJPDAsJiraFamilyEnabled}
              />
            </div>
          )}
          <Form onSubmit={handleFormSubmit}>
            {({ formProps, submitting, getValues }) => (
              <form {...formProps} className="bill-date-info-form">
                {!!formError && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        message={formError || 'Something went wrong.'}
                        helpContent={<HelpContent />}
                      />
                    }
                  />
                )}
                {!loadingLinkedEntitlement && (
                  <>
                    <h4 className="bill-date-info-choose-message" data-testId="bill-date-info-choose-message">
                      {renderBillDateInfoMessage()}
                    </h4>
                    <div className="new-bill-date-container">
                      <div className="radio-button-container">
                        <Radio
                          isChecked={'new-bill-date' === radioValue}
                          onChange={(event) => handleRadioChange(event, getValues())}
                          name={`key-new-bill-date`}
                          value={'new-bill-date'}
                          testId="new-bill-date-radio"
                        />
                      </div>
                      <div className="new-bill-date-comp">
                        <FormSection>
                          <Field name="newSelectedBillDate" isRequired>
                            {({ fieldProps: { id, ...rest } }) => (
                              <Fragment>
                                {renderNewBillDateTite()}
                                <HighlightedDatePicker
                                  selectProps={{ inputId: id }}
                                  {...rest}
                                  dateFormat={NEW_DESIGN_DATE_FORMAT}
                                  placeholder={renderNewBillDatePlaceholder()}
                                  disabledDateFilter={handleDisableDateFilter}
                                  onChange={(selectedDate) => {
                                    setSelectedBillingDate(selectedDate);
                                    rest.onChange(selectedDate);
                                  }}
                                />
                              </Fragment>
                            )}
                          </Field>
                        </FormSection>
                      </div>
                    </div>
                    <div className="existing-subscriptions-container">
                      {renderMatchedSubscriptionsTitle()}
                      <div>{subscriptionsOnBillDate()}</div>
                    </div>
                  </>
                )}
                <FormFooter>
                  <ButtonGroup>
                    <Button onClick={() => closeSidebar()} testId="bill-cancel-button">
                      Cancel
                    </Button>
                    <LoadingButton
                      type="submit"
                      appearance="primary"
                      isDisabled={!selectedBillingDate || loadingLinkedEntitlement}
                      testId="bill-change-button"
                      isLoading={submitting || isProcessing}
                      aria-label="Change bill date"
                      className="change-bill-date-button"
                    >
                      {isDesignIteration1Enabled ? 'Confirm' : 'Change bill date'}
                    </LoadingButton>
                  </ButtonGroup>
                </FormFooter>
                <WarningDialog
                  isOpen={isConfirmDialogOpen}
                  onClose={() => setConfirmDialogOpen(false)}
                  onConfirm={() => onConfirmDialog()}
                  header={'Confirm bill date change'}
                  description={getDescription()}
                  okLabel={'Confirm'}
                  testId={'confirm-dialog-modal'}
                />
              </form>
            )}
          </Form>
        </div>
      </div>
    </>
  );
};

export default SetCoBillDate;
